<template>
  <div>
    <container
      :isModal="true"
      @close-modal="closeScreen()"
      :loading="stateLoading || !stateReady"
    >
      <template v-slot:header>
        <h3>Cadastro de Contrato com Transportador</h3>
      </template>
      <template v-slot:body>
        <template v-if="stateReady">
          <form @submit.prevent="submitSave">
            <entity-selector-cliente
              v-if="amIAllowed('view', 'root-cliente')"
              :cliente="entity.cliente"
              :canEdit="!entity.cliente?.idCliente && !stateBlocked"
              @confirm-selection="confirmSelectionCliente"
              :required="true"
              :parentLoading="stateBlocked"
            />
            <fieldset :disabled="stateBlocked">
              <div class="p-formgrid p-grid">
                <div class="p-field p-fluid p-col-12 p-md-12">
                  <entity-selector-transportador
                    :transportador="transportadorSelecionado"
                    :idTransportadorFiltered="
                      entity.idTransportador ?? undefined
                    "
                    :required="true"
                    :cliente="entity.cliente"
                    @confirm-selection="confirmSelectionTransportador"
                  />
                </div>

                <div class="p-field p-fluid p-col-12 p-md-6">
                  <entity-selector-orgao-aet
                    :idOrgao="entity.idOrgao ?? undefined"
                    :required="true"
                    @after-set-orgao="afterSetOrgao"
                  />
                </div>

                <div class="p-field p-fluid p-col-12 p-md-6">
                  <input-with-label
                    label="Tipo de Solicitação"
                    :required="true"
                    v-slot="l"
                  >
                    <Dropdown
                      v-model="entity.tipoSolicitacao"
                      :options="tiposSolicitacao"
                      optionLabel="descricao"
                      optionValue="idTipoLicencaOrgao"
                      :showClear="true"
                      :id="l.id"
                      :required="l.required"
                      :placeholder="'&nbsp;'"
                    />
                  </input-with-label>
                </div>

                <div class="p-field p-fluid p-col-12 p-md-4">
                  <input-with-label
                    label="Preço Fixo (R$)"
                    :required="true"
                    v-slot="l"
                  >
                    <InputNumber
                      v-model="entity.precoFixo"
                      :id="l.id"
                      :required="l.required"
                      mode="currency"
                      currency="BRL"
                      locale="pt-BR"
                      :minFractionDigits="2"
                    />
                  </input-with-label>
                </div>

                <div class="p-field p-fluid p-col-12 p-md-3">
                  <input-with-label label="Início Vigência" v-slot="l">
                    <Calendar
                      v-model="entity.dataInicio"
                      :id="l.id"
                      dateFormat="dd/mm/yy"
                      :showIcon="true"
                    />
                  </input-with-label>
                </div>

                <div class="p-field p-fluid p-col-12 p-md-3">
                  <input-with-label label="Fim Vigência" v-slot="l">
                    <Calendar
                      v-model="entity.dataFim"
                      :id="l.id"
                      dateFormat="dd/mm/yy"
                      :showIcon="true"
                    />
                  </input-with-label>
                </div>

                <div class="p-field p-fluid p-col-12 p-md-2">
                  <input-with-label
                    label="Ativo"
                    v-slot="l"
                    :floatLabel="false"
                  >
                    <InputSwitch v-model="entity.flAtivo" :id="l.id" />
                  </input-with-label>
                </div>

                <div class="p-col-12">
                  <Button
                    type="submit"
                    class="p-button-rounded"
                    label="Salvar"
                  />
                </div>
              </div>
            </fieldset>
          </form>
        </template>
      </template>
    </container>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref,
  PropType
} from "vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { useToast } from "../../modules/message";
import {
  ContratoTransportadorPayload,
  useContratoTransportadorService
} from "../../modules/business/orcamento/contratoTransportadorBusiness";
import { useTipoLicencaOrgaoService } from "../../modules/business/licenca/tipoLicencaOrgaoBusiness";
import { TransportadorPayload } from "../../modules/business/transportador/transportadorBusiness";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import EntitySelectorOrgaoAet from "../common/EntitySelectorOrgaoAet.vue";
import EntitySelectorTransportador from "../transportador/EntitySelectorTransportador.vue";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";

interface ContratoCadastroState {
  entity: ContratoTransportadorPayload & { cliente?: ClientePayload | null };
  stateReady: boolean;
  savedEntity: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<ContratoCadastroState>({
      entity: useContratoTransportadorService().safeContrato(),
      savedEntity: false,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const stateLoading = computed(
      () => state.loadings.length !== 0 || props.parentLoading
    );

    const stateBlocked = computed(
      () =>
        !!(
          stateLoading.value ||
          (props.idContratoTransportador &&
            !amIAllowed("edit", state.entity.permissionInfo))
        )
    );

    const tiposSolicitacao = computed(() =>
      useTipoLicencaOrgaoService().getTiposLicencaOrgao()
    );

    const transportadorSelecionado = computed<TransportadorPayload | undefined>(
      () => {
        if (!state.entity.idTransportador) {
          return undefined;
        }
        return {
          idTransportador: state.entity.idTransportador,
          nome: state.entity.nomeTransportador ?? null,
          cpfCnpj: state.entity.cpfCnpjTransportador ?? null,
          municipio: {},
          contratoSocial: {},
          procuracao: {},
          usuario: {},
          transportadorOrgaos: []
        } as TransportadorPayload;
      }
    );

    const setEntity = (entity: ContratoTransportadorPayload) => {
      state.entity = useContratoTransportadorService().safeContrato(entity);
    };

    const closeScreen = () => {
      emit(
        "after-register-screen-closed",
        state.savedEntity ? state.entity : undefined
      );
    };

    const confirmSelectionCliente = (c: ClientePayload) => {
      if (c) {
        state.entity.cliente = c;
      }
    };

    const confirmSelectionTransportador = (t: TransportadorPayload | null) => {
      if (t && t.idTransportador) {
        state.entity.idTransportador = t.idTransportador;
        state.entity.nomeTransportador = t.nome ?? null;
        state.entity.cpfCnpjTransportador = t.cpfCnpj ?? null;
      } else {
        state.entity.idTransportador = null;
        state.entity.nomeTransportador = null;
        state.entity.cpfCnpjTransportador = null;
      }
    };

    const afterSetOrgao = (idOrgao: number | null) => {
      state.entity.idOrgao = idOrgao;
    };

    const submitSave = () => {
      const idProps = props.idContratoTransportador ?? 0;
      const isInsert = !idProps;
      const {
        saveNewContrato,
        updateContrato,
        loading,
        data
      } = useContratoTransportadorService();
      state.loadings.push(loading);
      (isInsert
        ? saveNewContrato(state.entity)
        : updateContrato(idProps, state.entity)
      )
        .then(() => {
          setEntity(data.value);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.savedEntity = true;
          useToast().success("Contrato salvo com sucesso");
          if (isInsert) {
            emit("after-save-new-entity", data.value.idContratoTransportador);
          }
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    onMounted(async () => {
      if (props.idContratoTransportador) {
        const {
          getContrato,
          loading,
          data
        } = useContratoTransportadorService();
        state.loadings.push(loading);
        getContrato(props.idContratoTransportador)
          .then(() => {
            setEntity(data.value);
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            state.stateReady = true;
          })
          .catch(() => {
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            closeScreen();
          });
      } else {
        if (props.cliente) {
          state.entity.cliente = props.cliente;
        }
        state.stateReady = true;
      }
    });

    return {
      submitSave,
      closeScreen,
      amIAllowed,
      confirmSelectionCliente,
      confirmSelectionTransportador,
      afterSetOrgao,
      tiposSolicitacao,
      transportadorSelecionado,
      stateLoading,
      stateBlocked,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente,
    EntitySelectorOrgaoAet,
    EntitySelectorTransportador
  },
  props: {
    idContratoTransportador: Number,
    cliente: Object as PropType<ClientePayload>,
    parentLoading: Boolean
  }
});
</script>

<style scoped lang="scss"></style>
